import {
  Loader2,
  CheckCircle2,
  XCircle,
  ChevronRight,
  ChevronDown,
  Database,
  Search,
  Code2,
  Beaker,
  Calculator,
  FileText,
  Settings,
  Globe,
} from 'lucide-react'
import { useState } from 'react'
import { cn } from '@/lib/utils'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { ToolResultRenderer } from './toolRenderers'
import { KeyValueList } from './toolRenderers/primitives'
import { ToolCallErrorBoundary } from './ToolCallErrorBoundary'

interface ToolCallDisplayProps {
  toolCallId: string
  toolName: string
  arguments?: string
  result?: string
  success?: boolean
  isExecuting?: boolean
}

// Tools that need special input rendering
const SEARCH_TOOLS = ['web_search_preview']
const FETCH_TOOLS = ['web_fetch']

export function ToolCallDisplay({
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  toolCallId,
  toolName,
  arguments: args,
  result,
  success,
  isExecuting = false,
}: ToolCallDisplayProps) {
  const [isExpanded, setIsExpanded] = useState(false)
  const [showRawOutput, setShowRawOutput] = useState(false)

  // Helper to safely parse JSON and unwrap result
  const safeJsonParse = (jsonString: string | undefined) => {
    if (!jsonString) return null
    try {
      const parsed = JSON.parse(jsonString)

      // If the parsed object has only a "result" key with a string value, unwrap it
      if (
        parsed &&
        typeof parsed === 'object' &&
        'result' in parsed &&
        typeof parsed.result === 'string'
      ) {
        try {
          // Try to parse the nested JSON string
          return JSON.parse(parsed.result)
        } catch {
          // If it fails, return the string as-is
          return parsed.result
        }
      }

      return parsed
    } catch {
      return jsonString // Return as-is if not valid JSON
    }
  }

  const parsedArgs = safeJsonParse(args)
  const parsedResult = safeJsonParse(result)

  // Extract effective tool name for renderer selection
  // For execute_lca_tool, use the inner tool_name from args
  const effectiveToolName = (() => {
    if (toolName === 'execute_lca_tool' && parsedArgs?.tool_name) {
      return parsedArgs.tool_name
    }
    return toolName
  })()

  // Get appropriate icon for tool type
  const getToolIcon = () => {
    const name = toolName.toLowerCase()
    // External MCP tools
    if (name === 'web_search_preview') return Search
    if (name === 'web_fetch') return Globe
    // EcoSemantic tools
    if (name.includes('search')) return Search
    if (name.includes('calculate') || name.includes('task')) return Calculator
    if (name.includes('read') || name.includes('get')) return FileText
    if (name.includes('method') || name.includes('biosphere')) return Beaker
    if (name.includes('database') || name.includes('ecoinvent') || name.includes('activit'))
      return Database
    if (name.includes('project') || name.includes('config') || name.includes('setting'))
      return Settings
    return Code2
  }

  // Humanize tool name for display
  const humanizeToolName = (name: string): string => {
    // Custom display names for external tools
    const displayNames: Record<string, string> = {
      web_search_preview: 'Web Search',
      web_fetch: 'Fetch Page',
    }
    if (displayNames[name]) return displayNames[name]

    return name
      .replace(/_/g, ' ')
      .replace(/([A-Z])/g, ' $1')
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ')
      .trim()
  }

  // Render input section based on tool type
  const renderInput = () => {
    if (!parsedArgs) return null

    // Search tools: show objective and queries
    if (
      SEARCH_TOOLS.includes(toolName) &&
      (parsedArgs.objective || parsedArgs.search_queries || parsedArgs.query)
    ) {
      const objective = parsedArgs.objective || parsedArgs.query || ''
      const queries: string[] = parsedArgs.search_queries || []
      return (
        <div className="space-y-1.5">
          <div className="text-[10px] font-semibold text-muted-foreground mb-1 uppercase tracking-wide">
            Search
          </div>
          {objective && (
            <div className="flex items-start gap-2 bg-muted/30 rounded-md px-3 py-2">
              <Search className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0 mt-0.5" />
              <span className="text-sm text-foreground">{objective}</span>
            </div>
          )}
          {queries.length > 0 && (
            <div className="flex flex-wrap gap-1.5 ml-1">
              {queries.map((q: string, i: number) => (
                <span
                  key={i}
                  className="text-xs bg-muted/50 text-muted-foreground px-2 py-0.5 rounded-full font-mono"
                >
                  {q}
                </span>
              ))}
            </div>
          )}
        </div>
      )
    }

    // Fetch tools: show URL prominently
    if (FETCH_TOOLS.includes(toolName) && (parsedArgs.urls || parsedArgs.url)) {
      const urls: string[] = parsedArgs.urls || [parsedArgs.url]
      const objective = parsedArgs.objective || ''
      return (
        <div className="space-y-1.5">
          <div className="text-[10px] font-semibold text-muted-foreground mb-1 uppercase tracking-wide">
            {urls.length > 1 ? 'URLs' : 'URL'}
          </div>
          {urls.map((u: string, i: number) => (
            <div key={i} className="flex items-center gap-2 bg-muted/30 rounded-md px-3 py-2">
              <Globe className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
              <a
                href={u}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-primary hover:underline truncate"
              >
                {u}
              </a>
            </div>
          ))}
          {objective && <p className="text-xs text-muted-foreground ml-1 italic">{objective}</p>}
        </div>
      )
    }

    // Default: KeyValueList for objects
    if (typeof parsedArgs === 'object') {
      return (
        <div>
          <div className="text-[10px] font-semibold text-muted-foreground mb-1.5 uppercase tracking-wide">
            Input
          </div>
          <KeyValueList
            data={parsedArgs as Record<string, unknown>}
            compact
            excludeKeys={['_hint']}
          />
        </div>
      )
    }

    // Non-object args
    return (
      <div>
        <div className="text-[10px] font-semibold text-muted-foreground mb-1 uppercase tracking-wide">
          Input
        </div>
        <pre className="font-mono bg-muted/30 p-2 rounded text-[11px] overflow-x-auto whitespace-pre-wrap break-words leading-relaxed max-w-full">
          {String(parsedArgs)}
        </pre>
      </div>
    )
  }

  const ToolIcon = getToolIcon()
  const displayName = humanizeToolName(toolName)

  return (
    <div
      className={cn(
        'my-2 rounded-lg border overflow-hidden transition-all',
        isExecuting && 'border-info/30 bg-info-muted/50',
        !isExecuting && success && 'border-success/30 bg-success-muted/50',
        !isExecuting && success === false && 'border-destructive/30 bg-destructive/5',
        !isExecuting && success === undefined && 'border-border bg-muted/20',
      )}
    >
      {/* Compact Header */}
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-muted/30 transition-colors"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            {/* Status Icon */}
            <div className="flex-shrink-0">
              {isExecuting && <Loader2 className="w-3.5 h-3.5 animate-spin text-info" />}
              {!isExecuting && success && <CheckCircle2 className="w-3.5 h-3.5 text-success" />}
              {!isExecuting && success === false && (
                <XCircle className="w-3.5 h-3.5 text-destructive" />
              )}
            </div>

            {/* Tool Icon & Name */}
            <ToolIcon className="w-3.5 h-3.5 text-muted-foreground" />
            <span className="text-sm font-medium flex-1 truncate">{displayName}</span>

            {/* Status Badge */}
            {isExecuting && (
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-info/10 text-info font-medium">
                Running
              </span>
            )}
            {!isExecuting && success && (
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-success/10 text-success font-medium">
                Done
              </span>
            )}
            {!isExecuting && success === false && (
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-destructive/10 text-destructive font-medium">
                Failed
              </span>
            )}

            {/* Expand indicator */}
            {isExpanded ? (
              <ChevronDown className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
            ) : (
              <ChevronRight className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
            )}
          </div>
        </TooltipTrigger>
        <TooltipContent side="top">
          <p>{isExpanded ? 'Click to collapse' : 'Click to view details'}</p>
        </TooltipContent>
      </Tooltip>

      {/* Expandable Details */}
      {isExpanded && (
        <div className="border-t border-border/50 px-3 py-3 space-y-3">
          {/* Input Arguments - use custom rendering */}
          {parsedArgs && renderInput()}

          {/* Result - Use smart renderer */}
          {parsedResult !== null && !isExecuting && (
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">
                  {success === false ? 'Error' : 'Output'}
                </span>
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    setShowRawOutput(!showRawOutput)
                  }}
                  className="text-[10px] text-muted-foreground hover:text-foreground transition-colors"
                >
                  {showRawOutput ? 'Show formatted' : 'Show raw'}
                </button>
              </div>

              {showRawOutput ? (
                <pre
                  className={cn(
                    'font-mono p-2 rounded text-[11px] overflow-x-auto max-h-[300px] overflow-y-auto',
                    'whitespace-pre-wrap break-words leading-relaxed max-w-full',
                    'bg-muted/30',
                  )}
                >
                  {typeof parsedResult === 'object'
                    ? JSON.stringify(parsedResult, null, 2)
                    : String(parsedResult)}
                </pre>
              ) : (
                <div className="max-h-[400px] overflow-y-auto">
                  <ToolCallErrorBoundary toolName={effectiveToolName}>
                    <ToolResultRenderer
                      toolName={effectiveToolName}
                      data={parsedResult}
                      success={success}
                    />
                  </ToolCallErrorBoundary>
                </div>
              )}
            </div>
          )}

          {/* Loading state for result */}
          {isExecuting && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>Processing...</span>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
